import { processAssignedTicket, processNominatedOrReviewTicket } from '../util/dataObjectToStoreObject';
import { UPDATE_TICKET, ADD_PREVIOUS_TICKETS, SET_SELECTED_BUSINESS_INFO } from './types';
import { handleError } from 'services/ErrorService/ErrorService';
import { fetchPastTicketsOnSameChannel } from '../api/historicalConversations';
import keyBy from 'lodash/keyBy';
import {
  extractSelectedTicketIdFromState,
  extractSelectedTicketFromState,
  extractSelectedInboxTab,
  isTicketAlreadyInQueue,
  isChatAlertDisabled,
  isNonChatAlertDisabled,
  extractTicketFromStateById,
  getSelectedQueue,
  extractSimplrTagForSelectedTicket,
  extractCurrentDecisionTreePath,
  getDecisionNodeAtPath,
  extractSelectedNonHistoricalTicketFromState,
} from '../extractFromState';
import { isLiveChat } from 'util/chat';
import {
  toggleChatAlertOnAndOff,
  toggleNonChatAlertOnAndOff,
  assignConversationToSpecialist,
  assignConversationToExpertAndSelect,
} from './index';
import { NOMINATED_TICKET_STATUSES } from '../constants/ticketStatuses';
import { INBOUND } from '../constants/messageDirection';
import { TICKET_ACTION_SOURCES } from '../analytics/ticketEventAnalytics';
import { PARTNER_REACH_OUT_TYPES } from 'constants/partnerReachOut';
import makeRequest from 'api/makeRequest';
import environment from 'environment';
import { getSelectedOrderNumber, getSelectedLineItemsIds } from 'selectors/tools';
import isEmpty from 'lodash/isEmpty';
import { nominatedExpertQueue } from 'api/tickets/queries';
import { getConversationById } from 'api/tickets';
import { safePlayAudio } from 'util/audioUtil';

export const updateTicket = (conversation, queueName, isAdmin, expertId, otherParams = {}) => {
  return async (dispatch) => {
    const ticket =
      queueName === 'nominated'
        ? processNominatedOrReviewTicket(conversation)
        : processAssignedTicket(conversation, expertId);
    dispatch({
      type: UPDATE_TICKET,
      ticket,
      queueName,
      isAdmin,
      expertId,
      ...otherParams,
    });
  };
};

export const updateTicketInQueue = (conversation, queueName, isAdmin, otherParams = {}) => {
  return async (dispatch) => {
    if (queueName === 'nominated') {
      try {
        await dispatch(updateTicket(conversation, queueName, isAdmin, undefined, otherParams));
      } catch (error) {
        handleError({ error, customMessage: 'Error updating ticket in queue', showPopup: true });
      }
    } else if (queueName === 'all' && NOMINATED_TICKET_STATUSES.includes(conversation.status)) {
      // await dispatch(updateTicket(conversation, queueName, isAdmin));
      // to process a ticket with a status of nominated and have the nominated expert associated with that ticket - see processAssignedTicket @src/util/util.js
      const expertQueueObj = await nominatedExpertQueue(conversation.objectId);
      if (expertQueueObj) {
        const expertId = expertQueueObj.simplrExpertPtr.objectId;
        await dispatch(updateTicket(conversation, queueName, isAdmin, expertId, otherParams));
      }
    } else {
      dispatch(playNewMessageAlert(conversation));
      await dispatch(updateTicket(conversation, queueName, isAdmin, undefined, otherParams));
    }
  };
};

export const playNewMessageAlert = (conversationObj) => {
  return (dispatch, getState) => {
    const isInbound = conversationObj.lastMessageDirection === INBOUND;
    if (isInbound) {
      const newUnreadCount = conversationObj.unreadCount;
      const oldUnreadCount = extractTicketFromStateById(getState(), conversationObj.objectId)
        ? extractTicketFromStateById(getState(), conversationObj.objectId).unreadCount
        : null;
      const newMessage = oldUnreadCount !== newUnreadCount;
      const ticketInAssignedTab = isTicketAlreadyInQueue(getState(), 'assigned', conversationObj.objectId);
      const ticketInLiveChatTab = isTicketAlreadyInQueue(getState(), 'live_chat', conversationObj.objectId);
      const ticketInQueue = ticketInAssignedTab || ticketInLiveChatTab;
      const liveChat = isLiveChat(conversationObj.communicationType);
      const alertEnabled = liveChat ? !isChatAlertDisabled(getState()) : !isNonChatAlertDisabled(getState());
      const selectedQueue = getSelectedQueue(getState());

      if (ticketInQueue && alertEnabled && newMessage) {
        const ticketNotSelected = extractSelectedTicketIdFromState(getState()) !== conversationObj.objectId;
        const isAssignedTabNotSelected = selectedQueue !== 'assigned';
        if (isAssignedTabNotSelected || ticketNotSelected) {
          if (liveChat) {
            const audioFile = '/sounds/chat-incoming-notification.mp3';
            const audio = new Audio(audioFile);
            safePlayAudio(audio);
            dispatch(toggleChatAlertOnAndOff());
          } else {
            const audioFile = '/sounds/non-chat-incoming-notification.mp3';
            const audio = new Audio(audioFile);
            safePlayAudio(audio);
            dispatch(toggleNonChatAlertOnAndOff());
          }
        }
      }
    }
  };
};

export const setSelectedBusinessInfo = (info) => ({
  type: SET_SELECTED_BUSINESS_INFO,
  info,
});

export const collectAndHydrateTicketsOnSameChannel = () => {
  return async (dispatch, getState) => {
    const selectedTicket = extractSelectedTicketFromState(getState());
    if (!selectedTicket) {
      return [];
    }

    const previousConversations = selectedTicket.previousConversations;
    if (previousConversations) {
      dispatch(hydrateHistoricalTickets(previousConversations));

      return [];
    }

    const conversation = await getConversationById(selectedTicket.ticketId);
    const shopName = conversation.platformDomain;
    const { tickets, totalCount } = await fetchPastTicketsOnSameChannel(selectedTicket, shopName);
    const ticketIds = tickets.map((t) => t.ticketId);
    const ticketIdsToTickets = keyBy(tickets, (t) => t.ticketId);

    const selectedTicketId = extractSelectedTicketIdFromState(getState());
    // checks to ensure ticket used in query is still the selected ticket
    if (selectedTicket.ticketId === selectedTicketId) {
      const actionPayload = {
        historicalTicketsTotalCount: totalCount,
        allTickets: ticketIds,
        ...ticketIdsToTickets,
      };

      dispatch({
        type: ADD_PREVIOUS_TICKETS,
        ticketId: selectedTicketId,
        previousConversations: actionPayload,
        queueName: extractSelectedInboxTab(getState()),
      });

      dispatch(hydrateHistoricalTickets(actionPayload));

      return tickets;
    }

    return [];
  };
};

export const hydrateHistoricalTickets = (tickets) => ({
  type: 'HYDRATE_HISTORICAL_TICKETS',
  historicalTickets: tickets,
});

export const addTicket = (ticket, queueName, isAdmin, lastCustomerMessageBody, nominationExpiresAt) => ({
  type: 'ADD_TICKET',
  ticket,
  queueName,
  isAdmin,
  lastCustomerMessageBody,
  nominationExpiresAt,
});

export const takeTicket =
  (conversationId, simplrAccountPtr, isAdmin, selectedInboxTab) => async (dispatch, getState) => {
    if (isAdmin) {
      await dispatch(assignConversationToSpecialist(conversationId, selectedInboxTab));
    } else {
      await dispatch(
        assignConversationToExpertAndSelect(conversationId, simplrAccountPtr, null, TICKET_ACTION_SOURCES.tagPicker)
      );
    }
  };

export const createPartnerReachOut = () => async (dispatch, getState) => {
  const state = getState();
  const ticket = extractSelectedNonHistoricalTicketFromState(state);
  const selectedOrderNumber = getSelectedOrderNumber(state);
  const selectedItemIds = getSelectedLineItemsIds(state);

  const payload = {
    type: PARTNER_REACH_OUT_TYPES.NETWORK_REQUEST,
    conversationId: ticket?.ticketId,
    simplrAccountId: ticket?.simplrAccountPtrId,
    simplrExpertId: state.user.id,
    simplrTag: extractSimplrTagForSelectedTicket(state),
    flowPath: extractCurrentDecisionTreePath(state),
    pendingField: getDecisionNodeAtPath(state)?.partnerTag,
    selectedOrderNumber: selectedOrderNumber ? selectedOrderNumber : undefined,
    selectedItemIds: !isEmpty(selectedItemIds) ? selectedItemIds : undefined,
  };
  try {
    await makeRequest(`${environment.app.serverApiUrl}/v1/partner-reach-out`, {
      method: 'post',
      data: payload,
    });
  } catch (error) {
    handleError({ error, showPopup: true, customFields: payload });
    throw error;
  }
};

export const updatePartnerReachOutStatus = (updatedStatus) => async (dispatch, getState) => {
  const state = getState();
  const ticket = extractSelectedNonHistoricalTicketFromState(state);

  const payload = {
    type: PARTNER_REACH_OUT_TYPES.NETWORK_REQUEST,
    conversationId: ticket?.ticketId,
    reachOutStatus: updatedStatus,
  };

  await makeRequest(`${environment.app.serverApiUrl}/v1/partner-reach-out`, {
    method: 'patch',
    data: payload,
  });
};
